import React, { useEffect, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { Link, NavLink } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardToolbar } from '@shared/components';
import { Button, Tab } from 'semantic-ui-react';
import ROUTES from '@shared/consts/routes';
import useMountWithTranslate from '@shared/utils/useMountWithTranslate';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from '@translations/translationNamespaces';
import FETCH_STATUS from '@shared/consts/fetchStatuses';
import PERMISSIONS from '@shared/consts/permissions';
import SCREEN_NAMES from '@shared/consts/screenNames';
import { isEmpty } from 'lodash';
import { TagsList } from '@screens/blog/components';

const Main = ({ status, tags, actions, permittedPermissions, isPermissionChecked, checkPermissionStatus }) => {
  const [t] = useTranslation(TRANSLATIONS.BLOG);
  const [isLoading, setIsLoading] = useState(true);

  const isUserPermitted = permission =>
    permittedPermissions?.find?.(permittedPermission => permittedPermission?.permission === permission)?.isPermitted;

  useMountWithTranslate(() => {
    setIsLoading(true);
    actions.getTags();
  });

  useEffect(() => {
    if (!isEmpty(permittedPermissions) && !isPermissionChecked?.[SCREEN_NAMES.BLOG]) {
      if (
        !isUserPermitted(PERMISSIONS.GET_BLOG_LIST) ||
        !isUserPermitted(PERMISSIONS.EDIT_BLOG) ||
        !isUserPermitted(PERMISSIONS.DELETE_BLOG) ||
        !isUserPermitted(PERMISSIONS.CREATE_BLOG) ||
        !isUserPermitted(PERMISSIONS.GET_BLOG_BY_ID) ||
        !isUserPermitted(PERMISSIONS.GET_TAGS_LIST) ||
        !isUserPermitted(PERMISSIONS.DELETE_TAGS) ||
        !isUserPermitted(PERMISSIONS.CREATE_TAGS) ||
        !isUserPermitted(PERMISSIONS.EDIT_TAGS) ||
        !isUserPermitted(PERMISSIONS.GET_TAGS_BY_ID)
      ) {
        actions.setIsPermissionCheckedAction(SCREEN_NAMES.BLOG);
        actions.checkPermissionAction([
          !isUserPermitted(PERMISSIONS.GET_BLOG_LIST) && PERMISSIONS.GET_BLOG_LIST,
          !isUserPermitted(PERMISSIONS.EDIT_BLOG) && PERMISSIONS.EDIT_BLOG,
          !isUserPermitted(PERMISSIONS.DELETE_BLOG) && PERMISSIONS.DELETE_BLOG,
          !isUserPermitted(PERMISSIONS.CREATE_BLOG) && PERMISSIONS.CREATE_BLOG,
          !isUserPermitted(PERMISSIONS.GET_BLOG_BY_ID) && PERMISSIONS.GET_BLOG_BY_ID,
          !isUserPermitted(PERMISSIONS.GET_TAGS_LIST) && PERMISSIONS.GET_TAGS_LIST,
          !isUserPermitted(PERMISSIONS.DELETE_TAGS) && PERMISSIONS.DELETE_TAGS,
          !isUserPermitted(PERMISSIONS.CREATE_TAGS) && PERMISSIONS.CREATE_TAGS,
          !isUserPermitted(PERMISSIONS.EDIT_TAGS) && PERMISSIONS.EDIT_TAGS,
          !isUserPermitted(PERMISSIONS.GET_TAGS_BY_ID) && PERMISSIONS.GET_TAGS_BY_ID,
        ]);
      }
    }
  }, [permittedPermissions]);

  useEffect(() => {
    status !== FETCH_STATUS.IDLE && status !== undefined && setIsLoading(false);
  }, [status, actions]);

  useEffect(() => {
    return () => actions.clearStatus();
  }, [actions]);

  const refreshPage = () => actions.getTags();

  const tabs = [
    {
      menuItem: {
        as: NavLink,
        content: t('posts'),
        to: ROUTES.BLOG,
        exact: true,
        key: 'posts',
        name: 'posts',
      },
    },
    isUserPermitted(PERMISSIONS.GET_TAGS_LIST) && {
      ...{
        menuItem: {
          as: NavLink,
          content: t('tags'),
          to: ROUTES.BLOG_TAGS,
          exact: true,
          name: 'tags',
          key: 'tags',
        },
        pane: (
          <Tab.Pane key="tags">
            <TagsList
              tags={tags}
              isUserPermitted={isUserPermitted}
              isLoading={isLoading || status === FETCH_STATUS.LOADING || checkPermissionStatus === FETCH_STATUS.IDLE}
              deleteAction={actions.deleteTag}
            />
          </Tab.Pane>
        ),
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle title={t('mainTitle')} />
        <CardToolbar className="mb-5">
          <Button disabled={status === FETCH_STATUS.LOADING} onClick={refreshPage} color="black">
            {t('reload')}
          </Button>
          {isUserPermitted(PERMISSIONS.CREATE_TAGS) ? (
            <Button as={Link} primary to={ROUTES.ADD_BLOG_TAG}>
              {t('create')}
            </Button>
          ) : null}
        </CardToolbar>
      </CardHeader>
      <Tab activeIndex={1} renderActiveOnly={false} menu={{ attached: 'top' }} panes={tabs} />
    </Card>
  );
};

Main.propTypes = {
  status: string,
  tags: arrayOf(
    shape({
      id: string,
      name: shape({
        en: string,
        pl: string,
      }),
    }),
  ),
  actions: shape({
    getTags: func,
    clearStatus: func,
    deleteTag: func,
    setIsPermissionCheckedAction: func,
    checkPermissionAction: func,
  }),
  permittedPermissions: arrayOf(string),
  isPermissionChecked: shape({
    [SCREEN_NAMES.BLOG]: bool,
  }),
  checkPermissionStatus: string,
};

Main.defaultProps = {
  status: undefined,
  tags: [],
  actions: {
    getTags: () => {},
    clearStatus: () => {},
    deleteTagAction: () => {},
    setIsPermissionCheckedAction: () => {},
    checkPermissionAction: () => {},
  },
  permittedPermissions: [],
  isPermissionChecked: {
    [SCREEN_NAMES.BLOG]: false,
  },
  checkPermissionStatus: undefined,
};

export default Main;
